import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAccount, useSignMessage } from 'wagmi'
import { useConsoleRoleAcrossChains } from '@/hooks/chain/useConsoleRoleAcrossChains'
import { useOperatorRoleAcrossChains } from '@/hooks/chain/useOperatorRole'
import {
    fetchAuthMe,
    operatorSession,
    refreshAuthSession,
    requestOperatorNonce,
    verifyOperatorSession,
    type StoredOperatorSession,
} from '@/lib/api'
import {
    OperatorSessionContext,
    type OperatorSessionContextValue,
    type SessionStatus,
} from '@/hooks/useOperatorSession'
import type { OperatorRole } from '@/types/api'

// Silent refresh fires this long before the JWT's `exp`. Operator JWTs are
// 30-minute tokens; a 2-minute lead is comfortably
// inside that window but still tolerant of clock skew and tab-throttled timers.
const REFRESH_LEAD_MS = 2 * 60 * 1000
// Floor the refresh delay so we don't spam /v1/auth/refresh if the stored
// session is already within the lead window when the provider mounts.
const MIN_REFRESH_DELAY_MS = 5 * 1000

function sessionMatches(
    session: StoredOperatorSession | null,
    address?: string,
): session is StoredOperatorSession {
    if (!session || !address) return false
    return session.address.toLowerCase() === address.toLowerCase()
}

export default function OperatorSessionProvider({ children }: { children: React.ReactNode }) {
    const { address, status: walletStatus } = useAccount()
    // OPERATOR_ROLE write-permission map — feeds the player-action affordances.
    const { rolesByChain } = useOperatorRoleAcrossChains(address)
    // OperatorRoles console membership — the authoritative sign-in eligibility.
    const {
        rolesByChain: consoleEligibilityByChain,
        eligibleChainIds,
        isLoading: isLoadingRole,
    } = useConsoleRoleAcrossChains(address)
    const { signMessageAsync } = useSignMessage()

    const [session, setSession] = useState<StoredOperatorSession | null>(() =>
        operatorSession.get(),
    )
    const [isSigning, setIsSigning] = useState(false)
    const [signInError, setSignInError] = useState<string | undefined>(undefined)
    const attemptKeyRef = useRef<string>('')
    // The connected account as of the last commit. A sign-in that resolves after
    // the wallet moved to another account must not persist or surface anything.
    const accountRef = useRef(address)
    useEffect(() => {
        accountRef.current = address
    }, [address])

    const signIn = useCallback(async () => {
        if (!address || eligibleChainIds.length === 0) return
        const chainId = eligibleChainIds[0]
        setIsSigning(true)
        setSignInError(undefined)
        const accountChanged = () => accountRef.current?.toLowerCase() !== address.toLowerCase()
        // Drop what this attempt stored if the wallet has since moved to another account.
        const abandon = () => {
            if (sessionMatches(operatorSession.get(), address)) operatorSession.clear()
        }
        try {
            const nonce = await requestOperatorNonce({ chainId, address })
            const signature = await signMessageAsync({ message: nonce.message, account: address })
            const verified = await verifyOperatorSession({
                chainId,
                address,
                message: nonce.message,
                signature,
            })
            if (accountChanged()) return
            const stored: StoredOperatorSession = {
                token: verified.token,
                expiresAt: verified.expiresAt,
                chainIds: verified.chainIds,
                address,
                rolesByChain: verified.rolesByChain ?? {},
                roles: verified.roles ?? [],
            }
            operatorSession.set(stored)
            // Hydrate from /v1/auth/me so the context state is sourced from the
            // same endpoint the UI will re-read on reload. If it fails (eg. a
            // fresh JWT briefly rejected by a stale cache), fall back to the
            // verify response roles — the next refresh will reconcile.
            try {
                const me = await fetchAuthMe()
                const withMe: StoredOperatorSession = {
                    ...stored,
                    chainIds: me.chainIds,
                    rolesByChain: me.rolesByChain ?? {},
                    roles: me.roles,
                }
                if (accountChanged()) return abandon()
                operatorSession.set(withMe)
                setSession(withMe)
            } catch {
                if (accountChanged()) return abandon()
                setSession(stored)
            }
        } catch (err) {
            if (accountChanged()) return
            operatorSession.clear()
            setSession(null)
            setSignInError(err instanceof Error ? err.message : 'Sign-in failed')
        } finally {
            setIsSigning(false)
        }
    }, [address, eligibleChainIds, signMessageAsync])

    // Clear any stale session whenever the connected account changes or disconnects.
    // The React state is adjusted during render ("adjust state when a prop changes"),
    // so nothing from the previous account is ever rendered. Both guards converge: the
    // second pass sees the cleared state and does nothing.
    const [errorAddress, setErrorAddress] = useState(address)
    if (errorAddress !== address) {
        // A sign-in error belongs to the account that produced it.
        setErrorAddress(address)
        if (signInError !== undefined) setSignInError(undefined)
    }
    if (session && !sessionMatches(session, address)) {
        setSession(null)
        if (signInError !== undefined) setSignInError(undefined)
    }

    // External side effects of the same reset: drop the stored JWT that belongs to
    // another account, and forget the last sign-in attempt on disconnect.
    useEffect(() => {
        if (!address) attemptKeyRef.current = ''
        const stored = operatorSession.get()
        if (stored && !sessionMatches(stored, address)) operatorSession.clear()
    }, [address])

    // Auto-trigger sign-in once role check has passed and we don't have a valid session.
    useEffect(() => {
        if (!address || isLoadingRole) return
        if (eligibleChainIds.length === 0) return
        if (sessionMatches(session, address)) return
        if (isSigning || signInError) return

        const key = `${address}:${eligibleChainIds.join(',')}`
        if (attemptKeyRef.current === key) return
        attemptKeyRef.current = key
        void signIn()
    }, [address, isLoadingRole, eligibleChainIds, session, isSigning, signInError, signIn])

    // Silent JWT refresh: schedule a /v1/auth/refresh call ~2 minutes before
    // the stored token expires. On success we persist the rotated token and
    // re-hydrate roles (so a freshly-granted role appears within the refresh
    // cadence rather than requiring a re-sign). On failure we clear the
    // session; the top-level gate will fall back to ConnectGate.
    useEffect(() => {
        if (!session || !sessionMatches(session, address)) return
        const expiresAt = new Date(session.expiresAt).getTime()
        if (!Number.isFinite(expiresAt)) return
        const delay = Math.max(expiresAt - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS)

        let cancelled = false
        const handle = window.setTimeout(async () => {
            try {
                const rotated = await refreshAuthSession()
                if (cancelled) return
                const next: StoredOperatorSession = {
                    token: rotated.token,
                    expiresAt: rotated.expiresAt,
                    chainIds: rotated.chainIds,
                    address: session.address,
                    rolesByChain: rotated.rolesByChain ?? session.rolesByChain,
                    roles: rotated.roles ?? session.roles,
                }
                operatorSession.set(next)
                setSession(next)
            } catch {
                if (cancelled) return
                operatorSession.clear()
                setSession(null)
            }
        }, delay)

        return () => {
            cancelled = true
            window.clearTimeout(handle)
        }
    }, [session, address])

    const retrySignIn = useCallback(() => {
        setSignInError(undefined)
        attemptKeyRef.current = ''
        void signIn()
    }, [signIn])

    const signOut = useCallback(() => {
        operatorSession.clear()
        setSession(null)
        setSignInError(undefined)
        attemptKeyRef.current = ''
    }, [])

    const status: SessionStatus = useMemo(() => {
        if (walletStatus === 'disconnected') return 'disconnected'
        if (walletStatus === 'connecting' || walletStatus === 'reconnecting') return 'connecting'
        if (!address) return 'disconnected'
        if (isLoadingRole) return 'checking-role'
        if (eligibleChainIds.length === 0) return 'no-role'
        if (sessionMatches(session, address)) return 'ready'
        if (signInError) return 'session-failed'
        return 'signing-in'
    }, [walletStatus, address, isLoadingRole, eligibleChainIds.length, session, signInError])

    const value = useMemo<OperatorSessionContextValue>(
        () => ({
            status,
            address,
            rolesByChain,
            consoleEligibilityByChain,
            consoleRolesByChain: session?.rolesByChain ?? {},
            eligibleChainIds,
            sessionChainIds: session?.chainIds ?? [],
            roles: session?.roles ?? [],
            signInError,
            retrySignIn,
            signOut,
        }),
        [
            status,
            address,
            rolesByChain,
            consoleEligibilityByChain,
            eligibleChainIds,
            session,
            signInError,
            retrySignIn,
            signOut,
        ],
    )

    return (
        <OperatorSessionContext.Provider value={value}>{children}</OperatorSessionContext.Provider>
    )
}

// Story/test seam: renders children inside a fully-seeded session context without any
// network or wallet activity. Not used by application code — Storybook stories (and
// tests that prefer a real provider over a vi.mock) seed roles/status through it.
export function SeededOperatorSession({
    children,
    roles = [],
    status = 'ready',
    address = '0x0000000000000000000000000000000000000001',
}: {
    children: ReactNode
    roles?: OperatorRole[]
    status?: SessionStatus
    address?: `0x${string}`
}) {
    const value = useMemo<OperatorSessionContextValue>(
        () => ({
            status,
            address,
            rolesByChain: {},
            consoleEligibilityByChain: {},
            consoleRolesByChain: {},
            eligibleChainIds: [],
            sessionChainIds: [],
            roles,
            retrySignIn: () => {},
            signOut: () => {},
        }),
        [status, address, roles],
    )
    return (
        <OperatorSessionContext.Provider value={value}>{children}</OperatorSessionContext.Provider>
    )
}
